























































































































































































import { mapState } from "vuex";
import {
  Component,
  Prop,
  Provide,
  Watch,
  Mixins
} from "vue-property-decorator";
import { ActionTypes, UUID } from "@/data/enums";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums
import { TranslationsTypes } from "@/data/enums/translations";

// Types
import { IGateway, IGatewayProvider } from "@/models/gateways";
import { DataModules, DEFAULT_LIST_SCOPE } from "@/store/modules/data";
import { ITranslationForm } from "@/models/translations";
import { IUser } from "@/models/users";
import { IState } from "@/store";
import AdminBrandId from "@/mixins/adminBrandId";
import HasValidation from "@/mixins/HasValidation";
import { ValidationObserver } from "vee-validate";
import { GatewayAuthType } from "@/data/enums/gateway";
import OauthProvider from "@/components/app/admin/oauth/oauthProvider.vue";
import { IOAuthToken } from "@/models/oauth";
import { QUERY_PARAMS } from "@/data/constants";
import { LogoSize, LogoType } from "@/data/enums/logos";
import { ICurrency } from "@/models/constants";

interface IGatewayProviderForm {
  payment_instructions: string;
  gateway_provider_id: IGatewayProvider["id"] | null;
  name: string | null;
}

@Component({
  computed: {
    ...mapState({
      availableGateways(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.allGatewaysModule,
          scope: this.allGatewaysScope
        });
      },
      user(state: IState) {
        return state.user;
      }
    })
  },
  components: {
    "oauth-provider": OauthProvider,
    "gateway-provider-settings": () =>
      import(
        "@/components/app/admin/settings/payments/gatewayProviderSettings.vue"
      ),
    "gateway-provider-card-types-list": () =>
      import(
        "@/components/app/admin/settings/payments/gatewayProviderCardTypesList.vue"
      ),
    "gateway-provider-currencies-list": () =>
      import(
        "@/components/app/admin/settings/payments/gatewayProviderCurrenciesList.vue"
      ),
    "add-oauth-token-message": () =>
      import("@/components/app/admin/oauth/addOAuthTokenMessage.vue"),
    "oauth-tokens-list": () =>
      import("@/components/app/admin/oauth/oauthTokensList.vue")
  }
})
export default class AddEditGatewayModal extends Mixins(
  RequestPromises,
  AdminBrandId,
  HasValidation
) {
  public declare $refs: {
    provider: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // :getters
  public user!: IUser;

  @Prop({ default: null })
  public gateway!: IGateway;

  @Prop({ default: null })
  public gatewayProviderId!: IGatewayProvider["id"];

  @Prop({ default: ActionTypes.CREATE })
  public actionType!: string;

  @Provide()
  public form: IGatewayProviderForm = {
    name: "",
    payment_instructions: "",
    gateway_provider_id: null
  };

  public selectedOAuthToken: IOAuthToken | null = null;

  public gatewayProviderCardTypes: string[] = [];
  public gatewayProviderCurrencies: ICurrency["id"][] = [];
  public gatewayProviderSettings: { [key: string]: string } = {};
  public formClone!: IGatewayProviderForm;

  public initialGatewayProviderCardTypes: string[] = [];
  public initialGatewayProviderCurrencies: ICurrency["id"][] = [];
  public initialGatewayProviderSettingsValues: { [key: string]: string } = {};

  public gatewayProviders: IGatewayProvider[] = [];
  public gatewayProvider: IGatewayProvider | null = null;
  public translationsForm: ITranslationForm = {} as ITranslationForm;

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;
  public testInProgress = false;
  public testResponse = {};

  public cardTypesDataScope = DEFAULT_LIST_SCOPE;
  public cardTypesDataModule = DataModules.CARD_TYPES;

  public created() {
    if (this.contentIsAvailable) {
      this.isLoading = true;
      this.loadData();
    }
  }

  public loadData() {
    Promise.all([
      this.getCardTypes(),
      this.getGatewayProviders(),
      this.getBrandSettings(),
      this.$store.dispatch("constants/getCurrencies")
    ])
      .then(() => {
        this.initializeData();
        this.initialGatewayProviderSettingsValues = this.$_.clone(
          this.gatewayProviderSettings
        );
        this.initialGatewayProviderCurrencies = this.$_.clone(
          this.gatewayProviderCurrencies
        );
        this.initialGatewayProviderCardTypes = this.$_.clone(
          this.gatewayProviderCardTypes
        );

        this.requestError = false;
      })
      .catch(error => {
        this.$buefy.toast.open({
          message: error.message,
          type: "is-danger"
        });
        this.requestError = true;
      })
      .finally(() => {
        this.isLoading = false;
        this.isReloading = false;
      });
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getCardTypes() {
    return this.$store.dispatch(`data/${this.cardTypesDataModule}/list`, {
      scope: this.cardTypesDataScope,
      vm: this,
      params: {
        limit: 0,
        order: "name"
      }
    });
  }

  public async getBrandSettings() {
    if (!this.isCreate || this.brandId === UUID.ORG) {
      return;
    }
    const settings = await this.$store.dispatch(
      `data/${DataModules.BRANDS}/getBrandSettings`,
      {
        vm: this,
        id: this.brandId
      }
    );
    this.gatewayProviderCurrencies.push(settings.currency_id);
    this.initialGatewayProviderCurrencies.push(settings.currency_id);
  }

  public get selectedGatewaySettings() {
    const fields = this.$_.get(
      this.$_.find(
        this.gatewayProviders,
        provider => provider.id === this.form.gateway_provider_id
      ),
      "fields",
      []
    );

    return fields !== null ? fields : [];
  }

  public get showSettings() {
    if (!this.isOAuth2) return true;
    return !!this.selectedOAuthToken;
  }

  public get isOAuth2() {
    return this.gatewayProvider?.auth_type === GatewayAuthType.OAUTH2;
  }

  public get oauthQueryParams() {
    return [
      {
        key: QUERY_PARAMS.GATEWAY_PROVIDER_ID,
        value: this.$_.get(this.gatewayProvider, "id")
      }
    ];
  }

  public get formIsChanged() {
    return (
      !this.$_.isEqual(this.form, this.formClone) ||
      !this.$_.isEqual(
        this.gateway?.oauth_application_access_token_id,
        this.selectedOAuthToken?.id
      ) ||
      !this.$_.isEqual(
        this.gatewayProviderSettings,
        this.initialGatewayProviderSettingsValues
      ) ||
      !this.$_.isEqual(
        this.$_.sortBy(this.gatewayProviderCurrencies),
        this.$_.sortBy(this.initialGatewayProviderCurrencies)
      ) ||
      !this.$_.isEqual(
        this.$_.sortBy(this.gatewayProviderCardTypes),
        this.$_.sortBy(this.initialGatewayProviderCardTypes)
      ) ||
      !this.$_.isEmpty(this.translationsForm)
    );
  }

  public get formIsDisabled() {
    return (
      this.isProcessing ||
      !this.formIsChanged ||
      this.hasErrors ||
      this.isLoading
    );
  }

  public initializeData() {
    if (this.isCreate) {
      if (this.gatewayProviderId) {
        this.form.gateway_provider_id = this.gatewayProviderId;
        this.setGatewayProvider(this.form.gateway_provider_id);
      }
      return;
    }
    this.form.payment_instructions = this.gateway.payment_instructions;
    this.form.gateway_provider_id = this.gateway.gateway_provider_id;
    this.form.name = this.gateway.name;
    this.form = this.$_.cloneDeep(this.form);
    this.formClone = this.$_.cloneDeep(this.form);
    this.$_.forEach(this.gateway.currencies, currency => {
      this.gatewayProviderCurrencies.push(currency.currency_id);
    });
    this.$_.forEach(this.gateway.card_types, cardType => {
      this.gatewayProviderCardTypes.push(cardType.code);
    });
    this.$_.forEach(this.gateway.gateway_settings, gatewaySetting => {
      this.gatewayProviderSettings[gatewaySetting.field] = gatewaySetting.value;
    });
    this.setGatewayProvider(this.form.gateway_provider_id);
  }

  public setGatewayProvider(id) {
    this.gatewayProvider = (this.$_.find(this.gatewayProviders, ["id", id]) ||
      {}) as IGatewayProvider;
  }

  public async getGatewayProviders() {
    this.gatewayProviders = await this.$store.dispatch(
      `data/${DataModules.GATEWAYS_PROVIDERS}/list`,
      {
        vm: this,
        params: {
          order: "name",
          with: ["fields", "logos"].join(),
          limit: 0
        }
      }
    );
  }

  public async validateBeforeSubmit() {
    this.isProcessing = true;
    try {
      this.clearErrors();
      if (this.$_.isEmpty(this.gatewayProvider)) {
        this.validationErrors["gateway_provider_id"].push(
          `${this.$t("_.payment_gateway")} ${(this.$t(
            "_.is_required"
          ) as string).toLowerCase()}`
        );
        return;
      }
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }
      const gateway = await (this.isCreate
        ? this.createGateway()
        : this.updateGateway());

      this.$buefy.toast.open({
        message: this.$t(
          `_.object_successfully_${this.isCreate ? "added" : "updated"}`,
          { object: this.$t("_.payment_gateway") }
        ) as string
      });
      if (this.isCreate) {
        await this.assignGatewayToBrand(gateway.id);
      }
      this.$emit("success", gateway);
      this.$bus.$emit("gatewaysChanged");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public assignGatewayToBrand(gatewayId: IGateway["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.GATEWAYS}/changeBrandGateway`,
      {
        gatewayId,
        data: {
          brand_id: this.brandId,
          active: 1,
          visible: 1
        }
      }
    );
  }

  public async createGateway() {
    const data = this.prepareApiPayload({
      org_id: this.user.org_id
    });

    const gateway = await this.$store.dispatch(
      `data/${DataModules.GATEWAYS}/create`,
      {
        data
      }
    );
    await this.saveTranslations(gateway);
    return gateway;
  }

  public async saveTranslations(gateway: IGateway) {
    if (this.$_.isEmpty(this.translationsForm)) {
      return;
    }

    return this.$store.dispatch(`data/${DataModules.TRANSLATIONS}/update`, {
      id: gateway.id,
      objectCodeType: TranslationsTypes.GATEWAY,
      data: this.translationsForm
    });
  }

  public async updateGateway() {
    return await this.$store.dispatch(`data/${DataModules.GATEWAYS}/update`, {
      id: this.gateway.id,
      data: this.prepareApiPayload()
    });
  }

  public prepareApiPayload(data = {}) {
    return {
      ...this.form,
      ...(this.isOAuth2
        ? { application_access_token_uuid: this.selectedOAuthToken?.uuid }
        : {}),
      card_types: this.gatewayProviderCardTypesFormatted,
      currencies: this.gatewayProviderCurrenciesFormatted,
      gateway_settings: this.gatewayProviderSettingsFormatted,
      ...data
    };
  }

  @Watch("form.gateway_provider_id")
  public onProviderChange() {
    this.gatewayProviderSettings = this.$_.clone(
      this.initialGatewayProviderSettingsValues
    );
    this.gatewayProviderCurrencies = this.$_.clone(
      this.initialGatewayProviderCurrencies
    );
    this.gatewayProviderCardTypes = this.$_.clone(
      this.initialGatewayProviderCardTypes
    );
  }

  public get gatewayProviderCurrenciesFormatted() {
    const formatted: Array<{ currency_id: ICurrency["id"] }> = [];
    this.$_.forEach(this.gatewayProviderCurrencies, currencyId => {
      formatted.push({
        currency_id: currencyId
      });
    });
    return formatted;
  }

  public get gatewayProviderSettingsFormatted() {
    const formatted: Array<{ field: string; value: string }> = [];
    for (const key in this.gatewayProviderSettings) {
      formatted.push({
        field: key,
        value: this.gatewayProviderSettings[key]
      });
    }
    return formatted;
  }

  public get hasEmptySetting() {
    if (
      this.$_.isEmpty(this.gatewayProviderSettings) ||
      this.selectedGatewaySettings.length !==
        Object.keys(this.gatewayProviderSettings).length
    ) {
      return true;
    }
    for (const key in this.gatewayProviderSettings) {
      if (this.$_.isEmpty(this.gatewayProviderSettings[key])) {
        return true;
      }
    }
    return false;
  }

  public get gatewayProviderCardTypesFormatted() {
    const formatted: Array<{ code: string }> = [];
    this.$_.forEach(this.gatewayProviderCardTypes, cardType => {
      formatted.push({
        code: cardType
      });
    });
    return formatted;
  }

  public get isCreate() {
    return this.actionType === ActionTypes.CREATE;
  }

  @Watch("gatewayProvider")
  public gatewayChanged(gatewayProvider) {
    this.form.gateway_provider_id = gatewayProvider.id;
    this.clearErrors();
  }

  public async openTranslateModal(props) {
    if (!this.canTranslate) return;
    this.$store.dispatch(
      `data/${DataModules.TRANSLATIONS}/openTranslationsModal`,
      {
        props: {
          saveOnSubmit: !this.isCreate,
          objectId: this.$_.get(this.gateway, "id", null),
          translationsForm: this.translationsForm,
          objectTypeCode: TranslationsTypes.GATEWAY,
          brandId: this.brandId,
          fieldType: "text",
          ...props
        },
        events: {
          "translations-saved": (translationsForm: ITranslationForm) => {
            this.translationsForm = this.$_.cloneDeep(translationsForm);
          }
        }
      }
    );
  }

  public get canTranslate() {
    return (
      this.$userCan("create_object_translation") &&
      this.$userCan("get_object_translation") &&
      this.$userCan("list_object_translations")
    );
  }

  public get guardFunctionalities() {
    return ["credit_card_types_list", "list_payment_gateway_providers"];
  }

  public get canManageGateway() {
    return this.isCreate
      ? this.$userCan("create_payment_gateway")
      : this.$userCan("update_payment_gateway");
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get getGuardFunctionalities() {
    return (
      this.$userCan("credit_card_types_list") &&
      this.$userCan("list_payment_gateway_providers")
    );
  }

  public get canTestCredentials() {
    return this.$userCan("test_brand_provider_credentials");
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.cardTypesDataModule,
      scope: this.cardTypesDataScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.GATEWAYS_PROVIDERS,
      vm: this
    });
  }

  public async testCredentials() {
    if (!this.canTestCredentials || !this.providerCanBeTested) {
      return;
    }
    this.testInProgress = true;
    try {
      this.testResponse = await this.$store.dispatch(
        `data/${DataModules.GATEWAYS_PROVIDERS}/testCredentials`,
        {
          id: this.form.gateway_provider_id,
          data: {
            gateway_settings: this.gatewayProviderSettingsFormatted
          }
        }
      );
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.testInProgress = false;
    }
  }

  @Watch("gatewayProviderSettings", { deep: true })
  public gatewaySettingsChanged() {
    this.testResponse = {};
  }

  public get providerCanBeTested() {
    const auth_type = this.$_.get(this.gatewayProvider, "auth_type", null);

    return auth_type === GatewayAuthType.SETTINGS;
  }

  public getProviderLogoUrl(provider: IGatewayProvider) {
    return this.$_.get(
      this.$_.find(provider.logos || [], logo => {
        if (logo.type === LogoType.ICON && logo.size === LogoSize.ONE_X)
          return true;
        return false;
      }),
      "url",
      null
    );
  }
}
